/**
 * CommandPalette — minimal Cmd+K command palette.
 *
 * 2026-09-18 (PROMPT F 21) — Closes Gap 2 of F 20 follow-up.
 *
 * F 19 Phase 6a design (operator-approved, F 20 dispatch):
 *   - Settings + palette only (sidebar chip deferred)
 *   - "Change Overlay" command opens the OverlayPicker
 *
 * This palette is intentionally minimal — 2 commands (Change Overlay +
 * Toggle Theme), keyboard navigation, search filter. Other surfaces
 * (BlockPalette; and SlashCommandMenu, deleted in F 211 C) are unrelated
 * editor tools and were NOT touched.
 *
 * Mounted at App root with a window-level keydown handler that toggles
 * on Cmd+K (mac) or Ctrl+K (win/linux). Esc closes. ⏎ selects.
 *
 * Storage for the OverlayPicker is created lazily on first use of the
 * "Change Overlay" command. Same IndexedDBAdapter as Settings.tsx FaceTabContent.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  TextInput,
  Stack,
  Group,
  Text,
  Box,
  Kbd,
  Badge,
  useMantineColorScheme,
  useComputedColorScheme,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { OverlayPickerModal } from '../settings/OverlayPicker';
import { IndexedDBAdapter } from '../../../core/storage/adapters/IndexedDBAdapter';
import type { FacePreferenceStorage } from '../../../core/overlays/facePreference';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CommandPaletteProps {
  /** True when palette should be open. Controlled by parent. */
  opened: boolean;
  /** Called when the palette closes (Esc, click outside, command fired). */
  onClose: () => void;
}

interface PaletteCommand {
  id: string;
  label: string;
  hint: string;
  keywords: string[];
  group: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CommandPalette({ opened, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [storage, setStorage] = useState<FacePreferenceStorage | null>(null);
  // F 107 — marks a palette→picker handoff in flight. When a command
  // hands off, the palette is closed first (so it is not left mounted
  // underneath the picker) and the reset effect below must NOT clobber
  // `showPicker` on the way out.
  const handoffRef = useRef(false);
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });

  // Reset state on close.
  useEffect(() => {
    if (!opened) {
      setQuery('');
      if (handoffRef.current) {
        // Consume the handoff — the picker is taking over.
        handoffRef.current = false;
      } else {
        setShowPicker(false);
      }
    }
  }, [opened]);

  // Lazy-init storage when "Change Overlay" runs.
  // 2026-09-18 (PROMPT F 21) — IndexedDBAdapter constructor signature is
  // positional: (dbName, version, eventEmitter?). Settings.tsx uses
  // the same cast pattern (object form → cast) but the actual call here
  // uses positional args to match the real signature.
  // F 108 — IndexedDBAdapter's constructor does NOT open the database;
  // `initialize()` does, and it is async. The old code constructed the
  // adapter and handed it straight to the picker, whose first `load()`
  // hit `ensureDb()` with `this.db === null` and threw
  // "IndexedDB not initialized" on every modal open. The try/catch could
  // not catch it because the constructor never throws.
  const ensureStorage = async (): Promise<FacePreferenceStorage | null> => {
    if (storage) return storage;
    try {
      const s = new IndexedDBAdapter('freshcards', 1);
      await s.initialize();
      const cast = s as unknown as FacePreferenceStorage;
      setStorage(cast);
      return cast;
    } catch (err) {
      console.warn('[CommandPalette] face preference storage unavailable', err);
      return null;
    }
  };

  // Commands (memoised — depends only on color scheme).
  const commands = useMemo<PaletteCommand[]>(() => [
    {
      id: 'change.overlay',
      label: 'Change Overlay',
      hint: 'Pick a Face overlay (theme, layout, bundle)',
      keywords: ['face', 'theme', 'overlay', 'skin', 'appearance', 'accent'],
      group: 'Face',
    },
    {
      id: 'toggle.theme',
      label: computed === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme',
      hint: 'Toggle Mantine color scheme',
      keywords: ['theme', 'dark', 'light', 'mode', 'color', 'appearance'],
      group: 'Appearance',
    },
  ], [computed]);

  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      c.keywords.some(k => k.includes(q)) ||
      c.hint.toLowerCase().includes(q)
    );
  }, [commands, query]);

  const runCommand = async (id: string) => {
    if (id === 'change.overlay') {
      // F 107 — close the palette FIRST (synchronously), so it is
      // unmounted and out of the stacking context before the picker
      // renders. Previously only showPicker was set, leaving the palette
      // mounted at z-index 1000 underneath the picker.
      //
      // F 108 — the close is deliberately synchronous and happens BEFORE
      // the async IndexedDB open, so the UI never shows a palette that is
      // waiting on I/O. handoffRef stops the reset-on-close effect from
      // clobbering showPicker if storage resolves after the unmount.
      handoffRef.current = true;
      onClose();
      const s = await ensureStorage();
      if (s) setShowPicker(true);
      return;
    }
    if (id === 'toggle.theme') {
      setColorScheme(computed === 'dark' ? 'light' : 'dark');
      onClose();
      return;
    }
  };

  return (
    <>
      <Modal
        opened={opened && !showPicker}
        onClose={onClose}
        size="lg"
        withCloseButton={false}
        padding={0}
        data-testid="command-palette-modal"
      >
        <Stack gap={0}>
          <Box p="sm">
            <TextInput
              data-testid="palette-search"
              placeholder="Type a command or search…"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSectionWidth={70}
              rightSection={
                <Group gap={4} wrap="nowrap">
                  <Kbd>esc</Kbd>
                </Group>
              }
              autoFocus
            />
          </Box>
          <Box style={{ maxHeight: 360, overflowY: 'auto' }}>
            <Stack gap={2} p="xs">
              {filtered.length === 0 ? (
                <Text c="dimmed" size="sm" p="md">
                  No commands match “{query}”
                </Text>
              ) : filtered.map((c) => (
                <Box
                  key={c.id}
                  data-testid={`palette-cmd-${c.id}`}
                  role="button"
                  tabIndex={0}
                  aria-label={c.label}
                  onClick={() => { void runCommand(c.id); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void runCommand(c.id);
                    }
                  }}
                  style={{
                    padding: '8px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background =
                      'var(--mantine-color-default-hover)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Box>
                      <Text size="sm" fw={500}>{c.label}</Text>
                      <Text size="xs" c="dimmed">{c.hint}</Text>
                    </Box>
                    <Badge size="xs" variant="light">{c.group}</Badge>
                  </Group>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box
            p="xs"
            style={{
              borderTop: '1px solid var(--mantine-color-default-border)',
              background: 'var(--mantine-color-default-hover)',
            }}
          >
            <Group gap="xs" justify="space-between">
              <Text size="xs" c="dimmed">
                {filtered.length} command{filtered.length === 1 ? '' : 's'}
              </Text>
              <Group gap={4}>
                <Kbd>↵</Kbd>
                <Text size="xs" c="dimmed">select</Text>
              </Group>
            </Group>
          </Box>
        </Stack>
      </Modal>
      {storage && (
        <OverlayPickerModal
          opened={showPicker}
          onClose={() => {
            setShowPicker(false);
            onClose();
          }}
          storage={storage}
          scope={{ userId: 'current' }}
        />
      )}
    </>
  );
}

export default CommandPalette;
